import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { ContextualHint } from 'apps/aiAutomation/components/EngagementGoalEditor/components/ContextualHint'
import { useSelectedGoalData } from 'apps/aiAutomation/components/EngagementGoalEditor/hooks/useSelectedGoalData'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GoalEditorMode,
  GrowFollowersEditorStep,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { EngagementGoal, GoalStatus, GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'

import { EditorLayout } from '../EditorLayout'
import { EditorStepLayout } from '../EditorStepLayout'
import { PublishManager } from '../PublishManager'

import { MessageEditor } from './components/MessageEditor'
import { useGrowFollowersEditor } from './hooks/useGrowFollowersEditor'
import { MessageEditorFormValues } from './lib/types'

export const GrowFollowersEditor = forwardRef<SubmitRef<EngagementGoal>>((_, ref) => {
  const { stepsState, messageData, onMessageFinish, combineAllData } = useGrowFollowersEditor()

  const messageRef = useRef<SubmitRef<MessageEditorFormValues>>(null)

  const { mode } = useGoalEditorContext(GoalType.GROW_FOLLOWERS)
  const isEditMode = mode !== GoalEditorMode.READ

  const activeGoalsLimitReached = useAppSelector(
    AiAutomationEngagementGoalSelectors.getIsActiveGoalLimitReached,
  )
  const { selectedGoal } = useSelectedGoalData()
  const isPublished = selectedGoal ? selectedGoal.status === GoalStatus.ACTIVE : false
  const isPublishBlocked = activeGoalsLimitReached && !isPublished

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!messageRef.current?.submit) {
          return { isValid: false }
        }

        const message = await messageRef.current.submit()

        return combineAllData(message)
      },
    }),
    [combineAllData],
  )

  return (
    <div className="o-auto p-x p-y-md">
      <ContextualHint />

      <EditorLayout title={l.translate('Set it up in 2 quick steps')}>
        <EditorStepLayout
          number={1}
          step={GrowFollowersEditorStep.MESSAGE}
          stepsState={stepsState}
          isDimmed={
            stepsState.completed[GrowFollowersEditorStep.PUBLISH] &&
            stepsState.current === GrowFollowersEditorStep.PUBLISH
          }
        >
          <MessageEditor
            messageData={messageData}
            isActive={
              stepsState.current === GrowFollowersEditorStep.MESSAGE ||
              (isEditMode && stepsState.completed[GrowFollowersEditorStep.MESSAGE])
            }
            isPublishBlocked={isPublishBlocked}
            onFinish={onMessageFinish}
            ref={messageRef}
          />
        </EditorStepLayout>

        <EditorStepLayout
          number={2}
          step={GrowFollowersEditorStep.PUBLISH}
          stepsState={stepsState}
          className="m-t-lg"
          isDimmed={
            stepsState.completed[GrowFollowersEditorStep.MESSAGE] &&
            stepsState.current === GrowFollowersEditorStep.MESSAGE
          }
          isBlocked={isPublishBlocked}
        >
          <PublishManager
            type={GoalType.GROW_FOLLOWERS}
            isBlocked={isPublishBlocked}
            isActive={stepsState.current === GrowFollowersEditorStep.PUBLISH}
          />
        </EditorStepLayout>
      </EditorLayout>
    </div>
  )
})
